import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { Footer } from '@/components/layout/Footer';
import { Navbar } from '@/components/layout/Navbar';

export interface AppShellProps {
  children: ReactNode;
}

const LIVE_INTERVIEW_PATH = /^\/interviews\/[^/]+$/;

/**
 * The page frame: navigation, main landmark and footer.
 *
 * Live interview sessions render fullscreen (no nav/footer) so the room can
 * lock to the viewport like a Meet / Zoom call.
 */
export function AppShell({ children }: AppShellProps) {
  const { pathname } = useLocation();
  const isLiveInterview = LIVE_INTERVIEW_PATH.test(pathname);

  if (isLiveInterview) {
    return (
      <div className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden overscroll-none">
        <a
          href="#main-content"
          className="sr-only-focusable bg-brand-600 focus:top-4 focus:left-4 focus:z-[100] focus:rounded-lg focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
        >
          Skip to content
        </a>
        <main id="main-content" className="min-h-0 flex-1 overflow-hidden">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main-content"
        className="sr-only-focusable bg-brand-600 focus:top-4 focus:left-4 focus:z-[100] focus:rounded-lg focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>

      <Navbar />

      {/* Offsets the fixed header so content is never hidden underneath it. */}
      <main id="main-content" className="flex-1 pt-16">
        {children}
      </main>

      <Footer />
    </div>
  );
}
